import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AvatarWithNotification } from '@/components/ui/avatar-with-notification';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, User, Baby } from 'lucide-react';
import { ImageUploader } from '@/components/ImageUploader';
import { calculateAge } from '@/lib/utils';
import { PersonProfile } from '@/hooks/usePersonProfiles';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';

interface UnifiedFamilySectionProps {
  familyId: string;
  persons: PersonProfile[];
  familyMembers?: any[]; // Add familyMembers to get profile data
  onRefresh: () => void;
  onPersonCreate: (data: any) => Promise<void>;
  onPersonUpdate: (id: string, data: any) => Promise<void>;
  onPersonDelete: (id: string) => Promise<void>;
}

const UnifiedFamilySection = ({ 
  familyId, 
  persons, 
  familyMembers = [],
  onRefresh, 
  onPersonCreate,
  onPersonUpdate,
  onPersonDelete 
}: UnifiedFamilySectionProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { showConfirmation } = useNativeConfirmation();
  const { openModal } = useModalNavigation();
  const [deletingPersonId, setDeletingPersonId] = useState<string | null>(null);

  const familyPersons = persons.filter(p => p.category === 'family');
  const parents = familyPersons.filter(p => 
    p.family_role === 'father' || 
    p.family_role === 'mother' || 
    p.family_role === 'parent' || 
    p.family_role === 'spouse'
  );
  const children = familyPersons.filter(p => 
    p.family_role === 'son' || 
    p.family_role === 'daughter' || 
    p.family_role === 'child'
  );


  const handleEditPerson = (person: PersonProfile) => {
    openModal('editPerson', { id: person.id });
  };

  const handleDeletePerson = async () => {
    if (deletingPersonId) {
      await onPersonDelete(deletingPersonId);
      setDeletingPersonId(null);
    }
  };

  const handleAvatarUpdate = async (person: PersonProfile, avatarUrl: string) => {
    try {
      // For adults with profile_id, update profiles table directly for proper sync
      if (person.profile_id && (person.family_role === 'father' || person.family_role === 'mother' || person.family_role === 'parent' || person.family_role === 'spouse')) {
        const { error } = await supabase
          .from('profiles')
          .update({ avatar_url: avatarUrl })
          .eq('id', person.profile_id);
        
        if (error) throw error;
      } else {
        // For children or adults without profile_id, update person_profiles
        await onPersonUpdate(person.id, { avatar_url: avatarUrl });
      }
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la photo.",
        variant: "destructive",
      });
    }
  };

  const getAvatarUrl = (person: PersonProfile) => {
    // For adults with profile_id, prioritize profiles.avatar_url from familyMembers data
    if (person.profile_id && person.family_role && ['father', 'mother', 'parent', 'spouse'].includes(person.family_role)) {
      const familyMember = familyMembers?.find(fm => fm.profile_id === person.profile_id);
      // Prioritize profiles.avatar_url, fallback to person_profiles.avatar_url
      return familyMember?.profiles?.avatar_url || person.avatar_url || '';
    }
    // For children or adults without profile_id, use person_profiles.avatar_url
    return person.avatar_url || '';
  };

  const getIcon = (person: PersonProfile) => {
    if (person.family_role === 'son' || person.family_role === 'daughter' || person.family_role === 'child') {
      return person.date_of_birth && calculateAge(person.date_of_birth) < 13 ? Baby : User;
    }
    return User;
  };

  const getRoleBadge = (role?: string) => {
    switch (role) {
      case 'father': return 'Papa';
      case 'mother': return 'Maman';
      case 'son': return 'Fils';
      case 'daughter': return 'Fille';
      case 'grandfather': return 'Grand-père';
      case 'grandmother': return 'Grand-mère';
      case 'guardian_male': return 'Tuteur';
      case 'guardian_female': return 'Tutrice';
      // Legacy roles fallback
      case 'parent': return 'Parent';
      case 'spouse': return 'Conjoint';
      case 'child': return 'Enfant';
      case 'grandparent': return 'Grand-parent';
      case 'guardian': return 'Tuteur';
      default: return 'Famille';
    }
  };

  return (
    <div className="space-y-4">
      {/* Parents Section */}
      {parents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Parents</h3>
          <div className="space-y-2">
            {parents.map((parent) => {
              const isCurrentUser = parent.profile_id === user?.id;
              return (
                <div key={parent.id} className="flex items-center gap-3 py-2">
                  <ImageUploader
                    currentImageUrl={getAvatarUrl(parent)}
                    onImageUploaded={(url) => handleAvatarUpdate(parent, url)}
                    bucketPath="profile"
                    size="sm"
                  >
                    <AvatarWithNotification
                      src={getAvatarUrl(parent)}
                      alt={parent.first_name}
                      fallback={parent.first_name.charAt(0).toUpperCase()}
                      className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity text-xs"
                      personId={parent.id}
                      profileId={parent.profile_id}
                    />
                  </ImageUploader>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{parent.first_name} {parent.last_name}</span>
                      {isCurrentUser && (
                        <Badge variant="secondary" className="text-xs">
                          Moi
                        </Badge>
                      )}
                    </div>
                    {parent.date_of_birth && (
                      <p className="text-xs text-muted-foreground">
                        {calculateAge(parent.date_of_birth)} ans
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditPerson(parent)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        // Use native confirmation on mobile Capacitor app
                        const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
                        
                        if (isMobile && isCapacitor) {
                          const confirmed = await showConfirmation({
                            title: "Supprimer cette personne ?",
                            message: "Cette action est irréversible. La personne sera définitivement supprimée.",
                            okButtonTitle: "Supprimer",
                            cancelButtonTitle: "Annuler"
                          });
                          
                          if (confirmed) {
                            await onPersonDelete(parent.id);
                          }
                        } else {
                          setDeletingPersonId(parent.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Children Section */}
      {children.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Enfants</h3>
          <div className="space-y-2">
            {children.map((child) => {
              const IconComponent = getIcon(child);
              return (
                <div key={child.id} className="flex items-center gap-3 py-2">
                  <ImageUploader
                    currentImageUrl={getAvatarUrl(child)}
                    onImageUploaded={(url) => handleAvatarUpdate(child, url)}
                    bucketPath="profile"
                    size="sm"
                  >
                    <AvatarWithNotification
                      src={getAvatarUrl(child)}
                      alt={child.first_name}
                      fallback={<IconComponent className="h-4 w-4" />}
                      className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity text-xs"
                      personId={child.id}
                      profileId={child.profile_id}
                    />
                  </ImageUploader>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{child.first_name} {child.last_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {child.date_of_birth && (
                        <span>{calculateAge(child.date_of_birth)} ans</span>
                      )}
                      {child.gender && (
                        <span>• {child.gender === 'male' ? 'Garçon' : child.gender === 'female' ? 'Fille' : 'Autre'}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditPerson(child)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        // Use native confirmation on mobile Capacitor app
                        const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
                        
                        if (isMobile && isCapacitor) {
                          const confirmed = await showConfirmation({
                            title: "Supprimer cette personne ?",
                            message: "Cette action est irréversible. La personne sera définitivement supprimée.",
                            okButtonTitle: "Supprimer",
                            cancelButtonTitle: "Annuler"
                          });
                          
                          if (confirmed) {
                            await onPersonDelete(child.id);
                          }
                        } else {
                          setDeletingPersonId(child.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPersonId} onOpenChange={() => setDeletingPersonId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette personne ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La personne sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePerson}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnifiedFamilySection;